import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { depositsApi } from "../services/depositsApi";
import invoiceService from "../services/invoiceService";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  CurrencyEuroIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import { TransactionStatus } from "../types/payment";

const History: React.FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<TransactionStatus | "all">(
    "all",
  );
  const [dateFilter, setDateFilter] = useState<"all" | "7d" | "30d" | "90d">(
    "all",
  );
  const [expandedTransactions, setExpandedTransactions] = useState<Set<string>>(
    new Set(),
  );
  const [loadingDetails, setLoadingDetails] = useState<Set<string>>(new Set());
  const [transactionDetails, setTransactionDetails] = useState<
    Map<string, any>
  >(new Map());

  const {
    data: transactions,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["transactions", page, search, statusFilter, dateFilter],
    queryFn: () =>
      depositsApi.getTransactions({
        page,
        limit: 20,
        search: search || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        dateRange: dateFilter !== "all" ? dateFilter : undefined,
      }),
  });

  const getStatusIcon = (status: TransactionStatus) => {
    switch (status) {
      case "completed":
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case "pending":
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case "processing":
        return <ClockIcon className="h-5 w-5 text-primary-500" />;
      case "failed":
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case "cancelled":
        return <XCircleIcon className="h-5 w-5 text-gray-500" />;
      case "refunded":
        return <XCircleIcon className="h-5 w-5 text-orange-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: TransactionStatus) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case "completed":
        return `${baseClasses} bg-green-100 text-green-800`;
      case "pending":
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case "processing":
        return `${baseClasses} bg-primary-100 text-primary-800`;
      case "failed":
        return `${baseClasses} bg-red-100 text-red-800`;
      case "cancelled":
        return `${baseClasses} bg-gray-100 text-secondary`;
      case "refunded":
        return `${baseClasses} bg-orange-100 text-orange-800`;
      default:
        return `${baseClasses} bg-gray-100 text-secondary`;
    }
  };

  const handleExport = () => {
    // Implementation for exporting transactions
  };

  const handleDownloadInvoice = async (
    invoiceId: string,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation(); // Prevent row expansion toggle
    try {
      await invoiceService.downloadInvoice(invoiceId);
    } catch (error) {
      console.error("Failed to download invoice:");
      // Optionally show error toast/notification
    }
  };

  const toggleTransactionDetails = async (transactionId: string) => {
    const isExpanded = expandedTransactions.has(transactionId);

    if (isExpanded) {
      // Collapse
      const newExpanded = new Set(expandedTransactions);
      newExpanded.delete(transactionId);
      setExpandedTransactions(newExpanded);
    } else {
      // Expand and fetch details if not already cached
      const newExpanded = new Set(expandedTransactions);
      newExpanded.add(transactionId);
      setExpandedTransactions(newExpanded);

      if (!transactionDetails.has(transactionId)) {
        const newLoading = new Set(loadingDetails);
        newLoading.add(transactionId);
        setLoadingDetails(newLoading);

        try {
          const details = await depositsApi.getDepositDetails(transactionId);
          const newDetails = new Map(transactionDetails);
          newDetails.set(transactionId, details);
          setTransactionDetails(newDetails);
        } catch (error) {
        } finally {
          const newLoading = new Set(loadingDetails);
          newLoading.delete(transactionId);
          setLoadingDetails(newLoading);
        }
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-secondary">
          {t("history.title")}
        </h1>
        <button
          onClick={handleExport}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
          {t("history.export")}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t("history.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-secondary placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <FunnelIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as TransactionStatus | "all")
              }
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-secondary focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">{t("history.allStatuses")}</option>
              <option value="completed">{t("transaction.completed")}</option>
              <option value="pending">{t("transaction.pending")}</option>
              <option value="processing">{t("transaction.processing")}</option>
              <option value="failed">{t("transaction.failed")}</option>
              <option value="cancelled">{t("transaction.cancelled")}</option>
              <option value="refunded">{t("transaction.refunded")}</option>
            </select>
          </div>

          {/* Date Filter */}
          <div className="relative">
            <CalendarIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <select
              value={dateFilter}
              onChange={(e) =>
                setDateFilter(e.target.value as "all" | "7d" | "30d" | "90d")
              }
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-secondary focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">{t("history.allTime")}</option>
              <option value="7d">{t("history.last7Days")}</option>
              <option value="30d">{t("history.last30Days")}</option>
              <option value="90d">{t("history.last90Days")}</option>
            </select>
          </div>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setSearch("");
              setStatusFilter("all");
              setDateFilter("all");
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {t("history.clearFilters")}
          </button>
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-6">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse flex items-center space-x-4 p-4 border-b border-gray-200"
                >
                  <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                    <div className="h-3 bg-gray-200 rounded w-16"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <div className="text-red-500 mb-2">{t("history.failedToLoad")}</div>
            <button
              onClick={() => window.location.reload()}
              className="text-primary-600 hover:text-primary-800 font-medium"
            >
              {t("history.tryAgain")}
            </button>
          </div>
        ) : transactions?.deposits?.length ? (
          <div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("history.transaction")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("history.amountAndPoints")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("history.status")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("history.date")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("history.card")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("history.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.deposits.map((transaction) => {
                    const isExpanded = expandedTransactions.has(transaction.id);
                    const isLoading = loadingDetails.has(transaction.id);
                    const details = transactionDetails.get(transaction.id);

                    return (
                      <React.Fragment key={transaction.id}>
                        <tr
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() =>
                            toggleTransactionDetails(transaction.id)
                          }
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="mr-3">
                                {isExpanded ? (
                                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                                ) : (
                                  <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                                )}
                              </div>
                              {getStatusIcon(transaction.status)}
                              <div className="ml-4">
                                <div className="text-sm font-medium text-secondary">
                                  #{transaction.id}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {t("history.depositTransaction")}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="space-y-1">
                              <div className="flex items-center">
                                <CurrencyEuroIcon className="h-4 w-4 text-gray-400 mr-1" />
                                <span className="text-sm font-medium text-secondary">
                                  {transaction.amount.toFixed(2)}
                                </span>
                              </div>
                              <div className="text-xs text-primary-600 font-medium">
                                +{transaction.epointsAwarded.toLocaleString()}{" "}
                                ePoints
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={getStatusBadge(transaction.status)}
                            >
                              {transaction.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div>
                              {new Date(
                                transaction.createdAt,
                              ).toLocaleDateString()}
                            </div>
                            <div>
                              {new Date(
                                transaction.createdAt,
                              ).toLocaleTimeString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="text-sm text-secondary">
                                {transaction.stripeMetadata?.last4
                                  ? `•••• ${transaction.stripeMetadata.last4}`
                                  : "Card"}
                              </div>
                              {transaction.stripeMetadata?.brand && (
                                <div className="ml-2 text-xs text-gray-500 uppercase">
                                  {transaction.stripeMetadata.brand}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {transaction.status === "completed" &&
                              transaction.invoiceId && (
                                <button
                                  onClick={(e) =>
                                    handleDownloadInvoice(
                                      transaction.invoiceId!,
                                      e,
                                    )
                                  }
                                  className="inline-flex items-center text-primary-600 hover:text-primary-900"
                                  title={t("history.downloadInvoice")}
                                >
                                  <DocumentArrowDownIcon className="h-5 w-5" />
                                </button>
                              )}
                          </td>
                        </tr>

                        {/* Expanded Details Row */}
                        {isExpanded && (
                          <tr className="bg-white">
                            <td colSpan={6}>
                              {isLoading ? (
                                <div className="flex items-center justify-center py-8">
                                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                                  <span className="ml-2 text-sm text-gray-600">
                                    {t("history.loadingDetails")}
                                  </span>
                                </div>
                              ) : details ? (
                                <div className="bg-white text-white p-8 shadow-sm">
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    <div>
                                      <h5 className="text-sm font-medium text-gray-500 mb-2">
                                        {t("history.paymentInformation")}
                                      </h5>
                                      <div className="space-y-2 text-sm">
                                        <div>
                                          <span className="font-medium">
                                            {t("payment.amount")}:
                                          </span>{" "}
                                          €{details.amount?.toFixed(2)}
                                        </div>
                                        <div>
                                          <span className="font-medium">
                                            {t("history.currency")}:
                                          </span>{" "}
                                          {details.currency}
                                        </div>
                                        <div>
                                          <span className="font-medium">
                                            {t("history.epointsAwarded")}:
                                          </span>{" "}
                                          {details.epointsAwarded?.toLocaleString()}
                                        </div>
                                        <div>
                                          <span className="font-medium">
                                            {t("history.paymentMethod")}:
                                          </span>{" "}
                                          {details.paymentMethod}
                                        </div>
                                      </div>
                                    </div>

                                    <div>
                                      <h5 className="text-sm font-medium text-gray-500 mb-2">
                                        {t("history.transactionInfo")}
                                      </h5>
                                      <div className="space-y-2 text-sm">
                                        <div>
                                          <span className="font-medium">
                                            {t("history.status")}:
                                          </span>{" "}
                                          <span
                                            className={getStatusBadge(
                                              details.status,
                                            )}
                                          >
                                            {details.status}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="font-medium">
                                            {t("history.created")}:
                                          </span>{" "}
                                          {new Date(
                                            details.createdAt,
                                          ).toLocaleString()}
                                        </div>
                                        {details.completedAt && (
                                          <div>
                                            <span className="font-medium">
                                              {t("history.completed")}:
                                            </span>{" "}
                                            {new Date(
                                              details.completedAt,
                                            ).toLocaleString()}
                                          </div>
                                        )}
                                        {details.expiresAt && (
                                          <div>
                                            <span className="font-medium">
                                              {t("history.expires")}:
                                            </span>{" "}
                                            {new Date(
                                              details.expiresAt,
                                            ).toLocaleString()}
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    <div>
                                      <h5 className="text-sm font-medium text-gray-500 mb-2">
                                        {t("history.additionalInformation")}
                                      </h5>
                                      <div className="space-y-2 text-sm">
                                        {details.stripePaymentIntentId && (
                                          <div>
                                            <span className="font-medium">
                                              {t("history.paymentIntentId")}:
                                            </span>{" "}
                                            <span className="font-mono text-xs">
                                              {details.stripePaymentIntentId}
                                            </span>
                                          </div>
                                        )}
                                        {details.description && (
                                          <div>
                                            <span className="font-medium">
                                              {t("history.description")}:
                                            </span>{" "}
                                            {details.description}
                                          </div>
                                        )}
                                        {details.notes && (
                                          <div>
                                            <span className="font-medium">
                                              {t("history.notes")}:
                                            </span>{" "}
                                            {details.notes}
                                          </div>
                                        )}
                                        {details.stripeMetadata &&
                                          Object.keys(details.stripeMetadata)
                                            .length > 0 && (
                                            <div>
                                              <span className="font-medium">
                                                {t("history.cardInfo")}:
                                              </span>
                                              <div className="mt-1 text-xs">
                                                {details.stripeMetadata.brand &&
                                                  details.stripeMetadata
                                                    .last4 &&
                                                  `${details.stripeMetadata.brand.toUpperCase()} •••• ${
                                                    details.stripeMetadata.last4
                                                  }`}
                                              </div>
                                            </div>
                                          )}
                                        {/* Invoice Download Button */}
                                        {details.status === "completed" &&
                                          details.invoiceId && (
                                            <div className="mt-4">
                                              <button
                                                onClick={(e) =>
                                                  handleDownloadInvoice(
                                                    details.invoiceId,
                                                    e,
                                                  )
                                                }
                                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                              >
                                                <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                                                {t("history.downloadInvoice")}
                                              </button>
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center py-4 text-red-500">
                                  {t("history.failedToLoadDetails")}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {transactions.pagination.totalPages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("history.previous")}
                    </button>
                    <button
                      onClick={() =>
                        setPage(
                          Math.min(
                            transactions.pagination.totalPages,
                            page + 1,
                          ),
                        )
                      }
                      disabled={page === transactions.pagination.totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("history.next")}
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        {t("history.showing")}{" "}
                        <span className="font-medium">
                          {(page - 1) * 20 + 1}
                        </span>{" "}
                        {t("history.to")}{" "}
                        <span className="font-medium">
                          {Math.min(page * 20, transactions.pagination.total)}
                        </span>{" "}
                        {t("history.of")}{" "}
                        <span className="font-medium">
                          {transactions.pagination.total}
                        </span>{" "}
                        {t("history.results")}
                      </p>
                    </div>
                    <div>
                      <nav
                        className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                        aria-label="Pagination"
                      >
                        <button
                          onClick={() => setPage(Math.max(1, page - 1))}
                          disabled={page === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t("history.previous")}
                        </button>
                        <button
                          onClick={() =>
                            setPage(
                              Math.min(
                                transactions.pagination.totalPages,
                                page + 1,
                              ),
                            )
                          }
                          disabled={page === transactions.pagination.totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t("history.next")}
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-12 text-center">
            <CurrencyEuroIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary">
              {t("history.noTransactionsFound")}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {search || statusFilter !== "all" || dateFilter !== "all"
                ? t("history.adjustFilters")
                : t("history.makeFirstDeposit")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
